import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import Pagination from 'pages/parts/Pagination'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate, returnSign } from 'helpers/helpers'

// Источник изменения цены (entity_type записи истории) -> ключ перевода
const PRICE_SOURCE_NAMES = {
	'products': 'manually',
	'documents_in': 'good_reception',
	'document_preparation': 'preparation',
	'document_breakdown': 'breakdown',
	'wms_transfer_pos': 'moving_another_store',
	'balance_element_avecos': 'recalculation',
	'documents_price': 'price_change',
}

// Источник -> просмотр документа. Ручная правка и пересчёт документа не имеют,
// поэтому их строки некликабельны.
const PRICE_SOURCE_ROUTES = {
	'documents_in': '/documents-in/preview/',
	'document_preparation': '/documents-preparation/preview/',
	'document_breakdown': '/documents-breakdown/preview/',
	'wms_transfer_pos': '/service/idocuments-in/preview/',
	'documents_price': '/documents-price/preview/',
}

const PAGE_SIZE = 20

function PriceHistory({ show, product, posId, onHide }) {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [total, setTotal] = useState(0)

	async function getPriceHistory(page = 0) {
		var urlParams = ""
		urlParams += returnSign(urlParams) + 'pos_id=' + posId
		urlParams += returnSign(urlParams) + 'page=' + page
		urlParams += returnSign(urlParams) + 'size=' + PAGE_SIZE

		const response = await GET(`/products-price-history/${product.id}` + urlParams, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data.data)
			setTotal(response.data.total)
		}
	}

	function paginate(page) {
		getPriceHistory(page.selected)
	}

	// Себестоимость выросла — красным, упала — зелёным. Первая запись сравнивать не с чем.
	function renderDiff(diff) {
		if (diff === null || diff === undefined) return <span className="text-muted">-</span>

		return (
			<span className={Number(diff) > 0 ? 'text-danger' : 'text-success'}>
				{Number(diff) > 0 && <span>+</span>}
				{formatMoney(diff, 2)}
			</span>
		)
	}

	function sourceName(item) {
		if (!item.entity_type) return t('document')

		return PRICE_SOURCE_NAMES[item.entity_type] ? t(PRICE_SOURCE_NAMES[item.entity_type]) : t('document')
	}

	function documentUrl(item) {
		if (!item.entity_id || !PRICE_SOURCE_ROUTES[item.entity_type]) return null

		return PRICE_SOURCE_ROUTES[item.entity_type] + item.entity_id
	}

	// Открываем в новой вкладке: история цен живёт в модалке поверх списка товаров,
	// уводить с него не хочется.
	function openDocument(item) {
		const url = documentUrl(item)
		if (!url) return

		window.open(url, '_blank', 'noopener')
	}

	useEffect(() => {
		if (show && product.id) {
			getPriceHistory()
		} else {
			setData([])
			setTotal(0)
		}
	}, [show, product.id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* MODAL PRICE HISTORY */}
			<Modal show={show} animation={false} centered size="xl" onHide={onHide}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('price_history')}
						{product.name && <span> - {product.name}</span>}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive table-fixed" style={{ 'height': '500px', 'overflow': 'auto' }}>
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('source')}</th>
									<th style={{ 'width': '15%' }}>{t('document')}</th>
									<th style={{ 'width': '15%' }} className="text-end text-nowrap">{t('cost_price')}</th>
									<th style={{ 'width': '10%' }} className="text-end text-nowrap">{t('change_amount')}</th>
									<th style={{ 'width': '15%' }} className="text-end text-nowrap">{t('sale_price')}</th>
									<th style={{ 'width': '15%' }}>{t('created_by')}</th>
									<th style={{ 'minWidth': '130px' }} className="text-center text-nowrap">{t('date')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => (
									<tr key={index}
										className={documentUrl(item) ? 'cursor' : ''}
										onClick={() => openDocument(item)}>
										<td className="text-center">{index + 1}</td>
										<td className="td-ellipsis">{sourceName(item)}</td>
										{/* id документа клиенту ни о чём не говорит — показываем название,
											а номер оставляем в подсказке для разбора спорных строк */}
										<td className="td-ellipsis"
											title={item.entity_id && item.entity_type !== 'products'
												? `${sourceName(item)} №${item.entity_id}`
												: ''}>
											{item.entity_id && item.entity_type !== 'products'
												? <span className={documentUrl(item) ? 'text-primary' : ''}>{sourceName(item)}</span>
												: <span className="text-muted">-</span>
											}
										</td>
										<td className="text-end">{formatMoney(item.cost_price, 2)}</td>
										<td className="text-end">{renderDiff(item.cost_price_diff)}</td>
										<td className="text-end">{formatMoney(item.sale_price, 2)}</td>
										<td className="td-ellipsis" title={item.created_by}>
											{item.created_by_name ? item.created_by_name : item.created_by}
										</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm:ss')}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{data.length === 0 &&
						<div className="text-center py-3">{t('no_data')}</div>
					}

					<div className="mt-2">
						<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} paginate={paginate} />
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL PRICE HISTORY */}
		</>
	)
}

export default PriceHistory
